import { LoadingOverlay } from "@/app/components/Loading";
import { FilterItem } from "./interface";
import { useQuery } from "@tanstack/react-query";
import { GetAcademicTerms } from "@/app/api/AcdemicTerms";
import React, { useCallback, useEffect, useState } from "react";
import queryStringHelper from "@/app/utils/queryStringHelper";
import { useRouter } from "next/navigation";
import { AcademicTerm } from "@anu-its/cat-grpc-pb/js/anu/cat_app/sas_pb";
import { timestampDate } from "@bufbuild/protobuf/wkt";
import {
  HierarchyMultiSelect,
  SingleSelect,
} from "@anu-its/ts-react-components";
import { titleCaseWord } from "@/app/utils/stringHelpers";
import { HierarchySelectOption } from "@anu-its/ts-react-components";

type SortedAcademicTerm = AcademicTerm & {
  period: string;
};

type AcademicTermDropdownInterface = FilterItem & {
  academicTermCodes?: string[];
  multiSelect?: boolean;
  includeCatTerms?: boolean;
};

const PERIOD_KEYS = ["current", "future", "past"];

const AcademicTermDropdown = ({
  updateFilter,
  academicTermCodes,
  multiSelect = false,
  includeCatTerms = true,
}: AcademicTermDropdownInterface) => {
  const [academicTermCode, setAcademicTermCode] = useState<string[]>(
    academicTermCodes || [],
  );
  const [selectAcademicTermData, setSelectAcademicTermData] = useState<
    { heading?: string; value: string; label: string }[]
  >([]);
  const [hierarchyOptions, setHierarchyOptions] = useState<
    HierarchySelectOption<string>[]
  >([]);
  const [initialized, setInitialized] = useState(false);

  const router = useRouter();

  useEffect(() => {
    if (academicTermCodes) {
      setAcademicTermCode(academicTermCodes);
    }
  }, [academicTermCodes]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["GetAcademicTerms", 2025, includeCatTerms],
    queryFn: () => GetAcademicTerms(2025, includeCatTerms),
  });

  const getExpandedTerms = useCallback(
    (selected: string[], hierarchy: HierarchySelectOption<string>[]) => {
      const expanded = new Set(selected);
      hierarchy.forEach((category) => {
        if (selected.includes(category.value)) {
          category.children?.forEach((child) => expanded.add(child.value));
        }
      });
      return Array.from(expanded);
    },
    [],
  );

  const getCompressedTerms = useCallback(
    (selected: string[], hierarchy: HierarchySelectOption<string>[]) => {
      const compressed = new Set(selected);
      hierarchy.forEach((category) => {
        const childValues = category.children?.map((c) => c.value) || [];
        if (
          childValues.length > 0 &&
          childValues.every((v) => selected.includes(v))
        ) {
          compressed.add(category.value);
          childValues.forEach((v) => compressed.delete(v));
        }
      });
      return Array.from(compressed);
    },
    [],
  );

  const getUISelection = useCallback(
    (selected: string[], hierarchy: HierarchySelectOption<string>[]) => {
      const uiState = new Set(selected);
      hierarchy.forEach((category) => {
        const childValues = category.children?.map((c) => c.value) || [];
        if (
          childValues.length > 0 &&
          childValues.every((v) => selected.includes(v))
        ) {
          uiState.add(category.value);
        }
        if (selected.includes(category.value)) {
          childValues.forEach((v) => uiState.add(v));
        }
      });
      return Array.from(uiState);
    },
    [],
  );

  const sortAcademicTerms = useCallback((academicTerms: AcademicTerm[]) => {
    return [...academicTerms].sort((a, b) => {
      const aTime = a.startDate ? timestampDate(a.startDate) : new Date();
      const bTime = b.startDate ? timestampDate(b.startDate) : new Date();
      return aTime.getTime() - bTime.getTime();
    });
  }, []);

  const getTermPeriod = useCallback((term: AcademicTerm) => {
    const now = new Date();
    const startDate = term.startDate ? timestampDate(term.startDate) : now;
    const endDate = term.endDate ? timestampDate(term.endDate) : now;

    // Create a new date object for the "effective" end date
    const effectiveEndDate = new Date(endDate);
    effectiveEndDate.setMonth(effectiveEndDate.getMonth() + 4);

    // Use the effective date for the "Past" check
    if (now > effectiveEndDate) return "past";
    if (now < startDate) return "future";

    return "current";
  }, []);

  const prepareAcademicTermOptions = useCallback(
    (academicTerms: AcademicTerm[]) => {
      const sortedTerms = sortAcademicTerms(academicTerms);

      return sortedTerms.map((term) => {
        return { ...term, period: getTermPeriod(term) };
      }) as SortedAcademicTerm[];
    },
    [getTermPeriod, sortAcademicTerms],
  );

  const formatSelectOptions = useCallback(
    (academicTerms: SortedAcademicTerm[]) => {
      const selectData: { heading?: string; value: string; label: string }[] =
        [];

      const groupedPeriods = academicTerms.reduce<
        Record<string, SortedAcademicTerm[]>
      >((acc, item) => {
        if (!acc[item.period]) acc[item.period] = [];
        acc[item.period].push(item);
        return acc;
      }, {});

      const sortOrder = ["current", "future", "past"];

      sortOrder.forEach((period) => {
        const items = groupedPeriods[period];
        if (items) {
          selectData.push({
            heading: titleCaseWord(period),
            value: titleCaseWord(period),
            label: titleCaseWord(period),
          });

          items.forEach((item) => {
            selectData.push({
              value: item.termCode,
              label: item.termDescription,
            });
          });
        }
      });

      return selectData;
    },
    [],
  );

  const formatHierarchyOptions = useCallback(
    (academicTerms: SortedAcademicTerm[]) => {
      const groupedPeriods = academicTerms.reduce<
        Record<string, SortedAcademicTerm[]>
      >((acc, item) => {
        if (!acc[item.period]) acc[item.period] = [];
        acc[item.period].push(item);
        return acc;
      }, {});

      const sortOrder = ["current", "future", "past"];
      const hierarchyData: HierarchySelectOption<string>[] = [];

      sortOrder.forEach((period) => {
        const items = groupedPeriods[period];
        if (items) {
          hierarchyData.push({
            value: period,
            label: titleCaseWord(period),
            selectable: true,
            children: items.map((item) => ({
              value: item.termCode,
              label: item.termDescription,
            })),
          });
        }
      });

      return hierarchyData;
    },
    [],
  );

  useEffect(() => {
    if (!(data instanceof Error) && data?.terms?.length && !initialized) {
      const academicTermOptions = prepareAcademicTermOptions(data.terms);
      const hierarchy = formatHierarchyOptions(academicTermOptions);
      setSelectAcademicTermData(formatSelectOptions(academicTermOptions));
      setHierarchyOptions(hierarchy);

      const academicTermQuery = queryStringHelper.getParam("academicTerm");
      const validTermCodes = academicTermOptions.map((term) => term.termCode);

      let initialSelection: string[];

      if (academicTermQuery) {
        initialSelection = academicTermQuery
          .split(",")
          .map((term) => term.trim())
          .filter(Boolean);
      } else {
        initialSelection = multiSelect ? ["current", "future"] : [];
      }

      const expanded = getExpandedTerms(initialSelection, hierarchy);
      const onlyTermCodes = expanded.filter((v) => !PERIOD_KEYS.includes(v));
      const uiState = getUISelection(expanded, hierarchy);
      const compressed = getCompressedTerms(expanded, hierarchy);

      const finalSelectedTerms = multiSelect
        ? uiState
        : [
            data.terms.filter(
              (t: AcademicTerm) => getTermPeriod(t) === "current",
            )[0]?.termCode || validTermCodes[0],
          ];

      setAcademicTermCode(finalSelectedTerms);
      setInitialized(true);

      const finalCompressed = multiSelect
        ? compressed
        : [finalSelectedTerms[0]];
      const filterTerms = multiSelect ? onlyTermCodes : [finalSelectedTerms[0]];

      updateFilter({ academicTermCode: filterTerms }, false);

      queryStringHelper.setParam(
        "academicTerm",
        multiSelect ? finalCompressed.join(",") : finalCompressed[0],
      );
    }
  }, [
    data,
    multiSelect,
    getTermPeriod,
    prepareAcademicTermOptions,
    formatSelectOptions,
    formatHierarchyOptions,
    updateFilter,
    initialized,
    getExpandedTerms,
    getCompressedTerms,
    getUISelection,
  ]);

  useEffect(() => {
    // Only perform the URL synchronization if we have loaded data and multiSelect is active
    if (multiSelect && data && !(data instanceof Error) && data.terms?.length) {
      const academicTermQuery = queryStringHelper.getParam("academicTerm");
      if (academicTermQuery !== null) {
        const queryTerms = academicTermQuery
          .split(",")
          .map((term) => term.trim())
          .filter(Boolean);

        const academicTermOptions = prepareAcademicTermOptions(data.terms);
        const hierarchy = formatHierarchyOptions(academicTermOptions);

        const expanded = getExpandedTerms(queryTerms, hierarchy);
        const uiState = getUISelection(expanded, hierarchy);

        // Simple comparison to prevent infinite loops if nothing has changed
        if (
          uiState.length !== academicTermCode.length ||
          !uiState.every((t) => academicTermCode.includes(t))
        ) {
          setAcademicTermCode(uiState);
        }
      }
    }
  }, [
    data,
    multiSelect,
    prepareAcademicTermOptions,
    formatHierarchyOptions,
    getExpandedTerms,
    getUISelection,
    academicTermCode,
  ]);

  if (error || data instanceof Error) {
    router.push("/error?errorType=data");
    return null;
  }

  if (isLoading || !initialized) return <LoadingOverlay />;

  return (
    <div>
      <label htmlFor="academic-terms" className="sr-only">
        Select an Academic Term
      </label>

      <div className="min-w-[320px] relative z-20">
        {multiSelect ? (
          <HierarchyMultiSelect
            selected={[...academicTermCode]}
            selectPlaceholderText="Academic Term(s)"
            options={hierarchyOptions}
            onSelectionChange={(selected: string[]) => {
              const expanded = getExpandedTerms(selected, hierarchyOptions);
              const uiState = getUISelection(expanded, hierarchyOptions);
              const compressed = getCompressedTerms(expanded, hierarchyOptions);

              const onlyTermCodes = expanded.filter(
                (v) => !PERIOD_KEYS.includes(v),
              );

              setAcademicTermCode(uiState);
              updateFilter({ academicTermCode: onlyTermCodes }, true);
              queryStringHelper.setParam("academicTerm", compressed.join(","));
            }}
          />
        ) : (
          <SingleSelect
            selected={academicTermCode[0]}
            selectPlaceholderText="Select an academic term"
            options={selectAcademicTermData ?? []}
            onSelectionChange={(selected: string) => {
              const singleValueArray = selected ? [selected] : [];
              setAcademicTermCode(singleValueArray);
              updateFilter({ academicTermCode: singleValueArray }, true);
              queryStringHelper.setParam("academicTerm", selected);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AcademicTermDropdown;
